import { planarSegmentsIntersect, projectPointToSegment } from '../shared/planar-geometry.js';
import { EARTH_RADIUS_KM, kilometersToMiles } from '../shared/geodesic-distance.js';
export { EARTH_RADIUS_KM, haversineKm } from '../shared/geodesic-distance.js';

export interface Coordinate {
  lat: number;
  lng: number;
}

export interface ProjectedPoint {
  x: number;
  y: number;
}

export interface AdaptiveGeodesicPath {
  coordinates: Coordinate[];
  projected: ProjectedPoint[];
  crossesAntimeridian: boolean;
}

interface AdaptiveGeodesicOptions {
  maxDepth?: number;
  maxScreenError?: number;
  maxScreenSegmentLength?: number;
}


export function geodesicPoints(lat1: number, lng1: number, lat2: number, lng2: number, n = 64): Coordinate[] {
  const φ1 = lat1 * Math.PI / 180, λ1 = lng1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180, λ2 = lng2 * Math.PI / 180;
  const dφ = φ2 - φ1, dλ = λ2 - λ1;
  const a = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  const d = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  if (d < 1e-10) return [{ lat: lat1, lng: lng1 }, { lat: lat2, lng: lng2 }];
  const pts: Coordinate[] = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
    const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
    const z = A * Math.sin(φ1) + B * Math.sin(φ2);
    pts.push({
      lat: Math.atan2(z, Math.sqrt(x * x + y * y)) * 180 / Math.PI,
      lng: Math.atan2(y, x) * 180 / Math.PI
    });
  }
  // Unwrap longitudes to keep geodesic points continuous across the antimeridian
  for (let i = 1; i < pts.length; i++) {
    const dLng = pts[i].lng - pts[i - 1].lng;
    if (dLng > 180) pts[i].lng -= 360;
    else if (dLng < -180) pts[i].lng += 360;
  }
  return pts;
}

function distanceToProjectedSegment(point: ProjectedPoint, start: ProjectedPoint, end: ProjectedPoint): number {
  return Math.sqrt(projectPointToSegment(point, start, end).distanceSq);
}

function unwrapLongitude(lng: number, reference: number): number {
  while (lng - reference > 180) lng -= 360;
  while (lng - reference < -180) lng += 360;
  return lng;
}

/** Builds only the geodesic samples that are needed for the current screen view. */
export function adaptiveGeodesicPath(
  start: Coordinate,
  end: Coordinate,
  project: (coordinate: Coordinate) => ProjectedPoint,
  options: AdaptiveGeodesicOptions = {},
): AdaptiveGeodesicPath {
  const maxDepth = options.maxDepth ?? 6;
  const maxScreenError = options.maxScreenError ?? 0.5;
  const maxScreenSegmentLength = options.maxScreenSegmentLength ?? 128;
  const phi1 = start.lat * Math.PI / 180;
  const lambda1 = start.lng * Math.PI / 180;
  const phi2 = end.lat * Math.PI / 180;
  const lambda2 = end.lng * Math.PI / 180;
  const deltaPhi = phi2 - phi1;
  const deltaLambda = lambda2 - lambda1;
  const a = Math.sin(deltaPhi / 2) ** 2
    + Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
  const distance = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const endLng = unwrapLongitude(end.lng, start.lng);
  const crossesAntimeridian = Math.abs(endLng - end.lng) > 1e-6;
  const unwrappedEnd = { lat: end.lat, lng: endLng };
  const startProjected = project(start);
  const endProjected = project(unwrappedEnd);
  if (distance < 1e-10) {
    return { coordinates: [start, unwrappedEnd], projected: [startProjected, endProjected], crossesAntimeridian };
  }

  const sinDistance = Math.sin(distance);
  const pointAt = (fraction: number): Coordinate => {
    const coefficientA = Math.sin((1 - fraction) * distance) / sinDistance;
    const coefficientB = Math.sin(fraction * distance) / sinDistance;
    const x = coefficientA * Math.cos(phi1) * Math.cos(lambda1) + coefficientB * Math.cos(phi2) * Math.cos(lambda2);
    const y = coefficientA * Math.cos(phi1) * Math.sin(lambda1) + coefficientB * Math.cos(phi2) * Math.sin(lambda2);
    const z = coefficientA * Math.sin(phi1) + coefficientB * Math.sin(phi2);
    const expectedLng = start.lng + (endLng - start.lng) * fraction;
    return {
      lat: Math.atan2(z, Math.sqrt(x * x + y * y)) * 180 / Math.PI,
      lng: unwrapLongitude(Math.atan2(y, x) * 180 / Math.PI, expectedLng),
    };
  };

  const coordinates: Coordinate[] = [start];
  const projected: ProjectedPoint[] = [startProjected];
  const refine = (
    startFraction: number,
    startPoint: Coordinate,
    startScreenPoint: ProjectedPoint,
    endFraction: number,
    endPoint: Coordinate,
    endScreenPoint: ProjectedPoint,
    depth: number,
  ): void => {
    const middleFraction = (startFraction + endFraction) / 2;
    const middlePoint = pointAt(middleFraction);
    const middleScreenPoint = project(middlePoint);
    const screenError = distanceToProjectedSegment(middleScreenPoint, startScreenPoint, endScreenPoint);
    const screenLength = Math.hypot(endScreenPoint.x - startScreenPoint.x, endScreenPoint.y - startScreenPoint.y);
    if (depth < maxDepth && (screenError > maxScreenError || screenLength > maxScreenSegmentLength)) {
      refine(startFraction, startPoint, startScreenPoint, middleFraction, middlePoint, middleScreenPoint, depth + 1);
      refine(middleFraction, middlePoint, middleScreenPoint, endFraction, endPoint, endScreenPoint, depth + 1);
      return;
    }
    coordinates.push(endPoint);
    projected.push(endScreenPoint);
  };

  refine(0, start, startProjected, 1, unwrappedEnd, endProjected, 0);
  return { coordinates, projected, crossesAntimeridian };
}

export function projectLatLngsToLocalKm(pts: Coordinate[]): ProjectedPoint[] {
  if (pts.length === 0) return [];
  const originLat = pts.reduce((sum, pt) => sum + pt.lat, 0) / pts.length;
  const originLng = pts.reduce((sum, pt) => sum + pt.lng, 0) / pts.length;
  const latScale = EARTH_RADIUS_KM * Math.PI / 180;
  const lngScale = latScale * Math.cos(originLat * Math.PI / 180);
  return pts.map((pt) => ({
    x: (pt.lng - originLng) * lngScale,
    y: (pt.lat - originLat) * latScale
  }));
}

function segmentsIntersect(a1: ProjectedPoint, a2: ProjectedPoint, b1: ProjectedPoint, b2: ProjectedPoint): boolean {
  return planarSegmentsIntersect(a1, a2, b1, b2, { epsilon: 1e-9, includeContact: true });
}

export function polygonSelfIntersects(projectedPts: ProjectedPoint[]): boolean {
  const n = projectedPts.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a1 = projectedPts[i];
    const a2 = projectedPts[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      if (j === i) continue;
      if ((i + 1) % n === j) continue;
      if (i === (j + 1) % n) continue;
      const b1 = projectedPts[j];
      const b2 = projectedPts[(j + 1) % n];
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

export function projectedPolygonAreaKm2(projectedPts: ProjectedPoint[]): number {
  if (projectedPts.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < projectedPts.length; i++) {
    const j = (i + 1) % projectedPts.length;
    area += projectedPts[i].x * projectedPts[j].y - projectedPts[j].x * projectedPts[i].y;
  }
  return Math.abs(area) / 2;
}

export function sphericalAreaKm2(pts: Coordinate[]): number {
  if (pts.length < 3) return 0;
  let area = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const lat1 = pts[i].lat * Math.PI / 180;
    const lat2 = pts[j].lat * Math.PI / 180;
    const dLng = (pts[j].lng - pts[i].lng) * Math.PI / 180;
    area += dLng * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  return Math.abs(area) * EARTH_RADIUS_KM * EARTH_RADIUS_KM / 2;
}

interface LineIntersection extends ProjectedPoint {
  t: number;
  u: number;
}

function lineLineIntersection(a1: ProjectedPoint, a2: ProjectedPoint, b1: ProjectedPoint, b2: ProjectedPoint): LineIntersection | null {
  const denom = (a1.x - a2.x) * (b1.y - b2.y) - (a1.y - a2.y) * (b1.x - b2.x);
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((a1.x - b1.x) * (b1.y - b2.y) - (a1.y - b1.y) * (b1.x - b2.x)) / denom;
  const u = ((a1.x - b1.x) * (a1.y - a2.y) - (a1.y - b1.y) * (a1.x - a2.x)) / denom;
  if (t <= 1e-9 || t >= 1 - 1e-9 || u <= 1e-9 || u >= 1 - 1e-9) return null;
  return { x: a1.x + t * (a2.x - a1.x), y: a1.y + t * (a2.y - a1.y), t, u };
}

interface HalfEdge {
  from: number;
  to: number;
  angle: number;
  used: boolean;
  reverse: number;
}

interface VertexPoint {
  x: number;
  y: number;
}

interface FaceData {
  idx: number;
  signedArea: number;
  halfEdges: number[];
}

interface DecomposedResult {
  area: number;
  faces: ProjectedPoint[][];
}

export function decomposedPolygonArea(projectedPts: ProjectedPoint[]): DecomposedResult {
  const n = projectedPts.length;
  if (n < 3) return { area: 0, faces: [] };

  const intersections: Array<{ edgeA: number; edgeB: number; x: number; y: number; t: number; u: number }> = [];
  for (let i = 0; i < n; i++) {
    const a1 = projectedPts[i];
    const a2 = projectedPts[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      const b1 = projectedPts[j];
      const b2 = projectedPts[(j + 1) % n];
      const inter = lineLineIntersection(a1, a2, b1, b2);
      if (inter) {
        intersections.push({ edgeA: i, edgeB: j, x: inter.x, y: inter.y, t: inter.t, u: inter.u });
      }
    }
  }

  if (intersections.length === 0) {
    return { area: projectedPolygonAreaKm2(projectedPts), faces: [] };
  }

  const vertices: VertexPoint[] = projectedPts.map((p) => ({ x: p.x, y: p.y }));
  function findVertex(x: number, y: number): number {
    for (let i = 0; i < vertices.length; i++) {
      if (Math.hypot(vertices[i].x - x, vertices[i].y - y) < 1e-9) return i;
    }
    vertices.push({ x, y });
    return vertices.length - 1;
  }

  const edgePoints: Array<Array<{ vertexId: number; t: number }>> = [];
  for (let i = 0; i < n; i++) {
    edgePoints.push([{ vertexId: i, t: 0 }]);
  }
  for (const inter of intersections) {
    const vid = findVertex(inter.x, inter.y);
    edgePoints[inter.edgeA].push({ vertexId: vid, t: inter.t });
    edgePoints[inter.edgeB].push({ vertexId: vid, t: inter.u });
  }
  for (let i = 0; i < n; i++) {
    edgePoints[i].push({ vertexId: (i + 1) % n, t: 1 });
    edgePoints[i].sort((a, b) => a.t - b.t);
  }

  const halfEdges: HalfEdge[] = [];
  for (let i = 0; i < n; i++) {
    const pts = edgePoints[i];
    for (let k = 0; k < pts.length - 1; k++) {
      const from = pts[k].vertexId;
      const to = pts[k + 1].vertexId;
      if (from === to) continue;
      const angleFwd = Math.atan2(vertices[to].y - vertices[from].y, vertices[to].x - vertices[from].x);
      const angleRev = Math.atan2(vertices[from].y - vertices[to].y, vertices[from].x - vertices[to].x);
      const fwdIdx = halfEdges.length;
      const revIdx = halfEdges.length + 1;
      halfEdges.push({ from, to, angle: angleFwd, used: false, reverse: revIdx });
      halfEdges.push({ from: to, to: from, angle: angleRev, used: false, reverse: fwdIdx });
    }
  }

  const outgoing = new Map<number, number[]>();
  for (let i = 0; i < halfEdges.length; i++) {
    const from = halfEdges[i].from;
    if (!outgoing.has(from)) outgoing.set(from, []);
    outgoing.get(from)!.push(i);
  }
  for (const [, list] of outgoing) {
    list.sort((a, b) => halfEdges[b].angle - halfEdges[a].angle);
  }

  const faces: number[][] = [];
  for (let i = 0; i < halfEdges.length; i++) {
    if (halfEdges[i].used) continue;
    const face: number[] = [];
    let current = i;
    let safety = 0;
    while (!halfEdges[current].used && safety++ < halfEdges.length * 2) {
      halfEdges[current].used = true;
      face.push(current);
      const rev = halfEdges[current].reverse;
      const target = halfEdges[current].to;
      const outList = outgoing.get(target)!;
      const revIndex = outList.indexOf(rev);
      if (revIndex === -1) break;
      const nextIdx = (revIndex + 1) % outList.length;
      current = outList[nextIdx];
      if (current === i) break;
    }
    if (face.length >= 3) {
      faces.push(face);
    }
  }

  const faceData: FaceData[] = faces.map((face, idx) => {
    const poly = face.map((heIdx) => vertices[halfEdges[heIdx].from]);
    let signedArea = 0;
    const m = poly.length;
    for (let k = 0; k < m; k++) {
      const j = (k + 1) % m;
      signedArea += poly[k].x * poly[j].y - poly[j].x * poly[k].y;
    }
    return { idx, signedArea, halfEdges: face };
  }).filter((f) => Math.abs(f.signedArea) >= 1e-15);

  if (faceData.length === 0) {
    return { area: projectedPolygonAreaKm2(projectedPts), faces: [] };
  }

  let exteriorIdx = 0;
  let minSignedArea = faceData[0].signedArea;
  for (let i = 1; i < faceData.length; i++) {
    if (faceData[i].signedArea < minSignedArea) {
      minSignedArea = faceData[i].signedArea;
      exteriorIdx = i;
    }
  }

  const heToFace = new Map<number, number>();
  for (let i = 0; i < faceData.length; i++) {
    for (const heIdx of faceData[i].halfEdges) {
      heToFace.set(heIdx, i);
    }
  }

  const filled = new Array(faceData.length).fill(false);
  const bfsQueue = [exteriorIdx];
  const visited = new Set([exteriorIdx]);
  let bfsHead = 0;
  while (bfsHead < bfsQueue.length) {
    const current = bfsQueue[bfsHead++];
    for (const heIdx of faceData[current].halfEdges) {
      const revIdx = halfEdges[heIdx].reverse;
      const neighbor = heToFace.get(revIdx);
      if (neighbor !== undefined && !visited.has(neighbor)) {
        visited.add(neighbor);
        filled[neighbor] = !filled[current];
        bfsQueue.push(neighbor);
      }
    }
  }

  const filledFaces: ProjectedPoint[][] = [];
  let totalArea = 0;
  for (let i = 0; i < faceData.length; i++) {
    if (filled[i]) {
      totalArea += Math.abs(faceData[i].signedArea) / 2;
      filledFaces.push(
        faceData[i].halfEdges.map((heIdx) => vertices[halfEdges[heIdx].from])
      );
    }
  }
  return { area: totalArea, faces: filledFaces };
}

interface FaceInfo {
  latLngs: Coordinate[];
  areaKm2: number;
  centroid: Coordinate;
}

export function getDecomposedPolygonInfo(pts: Coordinate[]): FaceInfo[] | null {
  const projectedPts = projectLatLngsToLocalKm(pts);
  if (!polygonSelfIntersects(projectedPts)) return null;
  const result = decomposedPolygonArea(projectedPts);
  if (!result.faces || result.faces.length === 0) return null;

  const originLat = pts.reduce((sum, pt) => sum + pt.lat, 0) / pts.length;
  const originLng = pts.reduce((sum, pt) => sum + pt.lng, 0) / pts.length;
  const latScale = EARTH_RADIUS_KM * Math.PI / 180;
  const lngScale = latScale * Math.cos(originLat * Math.PI / 180);

  return result.faces.map((face) => {
    const latLngs = face.map((p) => ({
      lat: originLat + p.y / latScale,
      lng: originLng + p.x / lngScale,
    }));

    let cx = 0;
    let cy = 0;
    let a = 0;
    const n = face.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const cross = face[i].x * face[j].y - face[j].x * face[i].y;
      cx += (face[i].x + face[j].x) * cross;
      cy += (face[i].y + face[j].y) * cross;
      a += cross;
    }
    a /= 2;
    cx /= 6 * a;
    cy /= 6 * a;

    return {
      latLngs,
      areaKm2: Math.abs(a) / 2,
      centroid: { lat: originLat + cy / latScale, lng: originLng + cx / lngScale }
    };
  });
}

export function polygonAreaKm2(pts: Coordinate[]): number {
  if (pts.length < 3) return 0;
  const projectedPts = projectLatLngsToLocalKm(pts);
  if (!polygonSelfIntersects(projectedPts)) {
    return sphericalAreaKm2(pts);
  }
  return decomposedPolygonArea(projectedPts).area;
}

export function formatPrimary(km: number, useMetric: boolean): string {
  if (useMetric) {
    return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(2)} km`;
  }
  const mi = kilometersToMiles(km);
  return mi < 0.1 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(2)} mi`;
}

export function formatArea(km2: number, useMetric: boolean): string {
  if (useMetric) {
    return km2 < 1 ? `${Math.round(km2 * 1e6)} m²` : `${km2.toFixed(2)} km²`;
  }
  const mi2 = km2 * 0.386102;
  return mi2 < 0.01
    ? `${Math.round(km2 * 247.105)} acres`
    : `${mi2.toFixed(2)} mi²`;
}
